// ABOUTME: Remembers WHEN this browser last visited after watching the brand intro film, so a
// ABOUTME: returning guest skips the opening — until 7 days pass without a visit (SPEC-43, R1).

// localStorage key. Matches the existing versai_* client-storage naming (versai_token,
// versai_consent, versai_wishlist_*). One key per deployment: each brand is served by its
// own frontend origin, so there is nothing to namespace against.
export const VISIT_STORAGE_KEY = 'versai_seen_intro';

// Shape of the stored value: JSON `{ v: VISIT_RECORD_VERSION, ts: <epoch ms> }`. Same idiom as the
// consent record (`useConsent.js`: `{ v, ts, categories }`). `ts` is the moment of the LAST VISIT,
// not of the first viewing — see the window note below.
export const VISIT_RECORD_VERSION = 1;

/**
 * The returning-guest window (SPEC-43, R1): how long a browser keeps skipping the opening film
 * with no visit in between. Measured from the last ENTRY, not from the first viewing — every entry
 * to the boutique re-stamps `ts` (see `touchIntroSeen`), so a guest who comes back every few days
 * never sees the film again, while one who stays away for a week gets the full first-time
 * sequence: film plus first-visit welcome.
 */
export const INTRO_MEMORY_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// The record is CREATED only once the film has actually finished (or been skipped) — NOT on entry.
// A guest who closes the tab halfway through the opening has not really seen it, and should get it
// again. Entry only REFRESHES an existing record (`touchIntroSeen`); it never creates one.
// Deliberately independent of CONSENT_VERSION: bumping the privacy copy must not resurrect the
// intro film for every existing guest.
//
// Legacy value. Before SPEC-43 this key held the bare literal '1' with no timestamp. Such a record
// deliberately reads as NOT seen: its age is unknown, and "unknown age" is treated as expired. The
// cost is one extra showing of the film for guests carrying the old marker, after which a
// timestamped record is written at the film's end and the window works normally from there. This
// mirrors `readConsent` treating an unknown record shape as absent, and is far simpler than
// guessing an age for a value that never had one.

/** Module-level so the default keeps a stable identity (same idiom as useManualStop). */
const defaultNow = () => Date.now();

/**
 * Read + validate the stored record. Returns it only if it is well-formed and at the current
 * version; otherwise null. Never throws — Safari private mode throws on `getItem`.
 */
function readRecord() {
  try {
    const raw = localStorage.getItem(VISIT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.v !== VISIT_RECORD_VERSION) return null;
    if (typeof parsed.ts !== 'number' || !Number.isFinite(parsed.ts)) return null;
    return parsed;
  } catch {
    // Malformed JSON (including the legacy literal '1', which parses to a bare number and fails
    // the shape check above) or storage disabled — either way, not a usable record.
    return null;
  }
}

/** Write `{ v, ts }`. Returns whether the write landed; never throws (quota / private mode). */
function writeRecord(ts) {
  try {
    localStorage.setItem(VISIT_STORAGE_KEY, JSON.stringify({ v: VISIT_RECORD_VERSION, ts }));
    return true;
  } catch {
    // Storage unavailable: the guest simply sees the intro again next time. Not worth failing
    // entry over.
    return false;
  }
}

/**
 * True when this browser completed the intro film AND has visited within the last
 * `INTRO_MEMORY_TTL_MS`. A record whose `ts` lies in the future (clock skew, a device whose
 * clock was set back) counts as seen just now rather than as garbage.
 *
 * @param {object}   [opts]
 * @param {Function} [opts.now] injectable clock (ms), for tests — never a global fake Date
 */
export function hasSeenIntro({ now = defaultNow } = {}) {
  const record = readRecord();
  if (!record) return false;
  // Negative age (future ts) passes this test too, which is the "seen-now" reading we want.
  return now() - record.ts <= INTRO_MEMORY_TTL_MS;
}

/**
 * Record that the intro film has been seen, stamped `now`. Still only called at film end/skip.
 * Idempotent (a re-mark just re-stamps); never throws.
 */
export function markIntroSeen({ now = defaultNow } = {}) {
  writeRecord(now());
}

/**
 * A visit happened: refresh the record's `ts` so the 7-day window restarts from today.
 *
 * Only refreshes a record that is CURRENTLY valid. It never creates one (a guest who has not
 * finished the film has nothing to refresh) and never resurrects an expired one (that guest is
 * about to see the film again, and its end will write a fresh record).
 *
 * @returns {boolean} whether the record was actually refreshed
 */
export function touchIntroSeen({ now = defaultNow } = {}) {
  if (!hasSeenIntro({ now })) return false;
  return writeRecord(now());
}

/** Test/QA helper — forget the visit so the next entry replays the intro. */
export function forgetIntroSeen() {
  try {
    localStorage.removeItem(VISIT_STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}
